import h from 'hyperscript';
import infoSvg from '@disy/cadenza-icons/info.svg';

import type { AttributeWithObjecttype } from 'ui/attribute-label/attribute-formatting-utils';
import { attributeObjecttypeLabel, attributePrintName } from 'ui/attribute-label/attribute-formatting-utils';
import { createResourceLabel } from 'ui/resource-label/resource-label';
import { createAttributeLabel } from 'ui/attribute-label/attribute-label';
import { createResourceSummary } from 'ui/resource-summary/resource-summary';
import type { DataSourceLabelOptions } from 'ui/resource-summary/resource-summary-utils';
import { createDataSourceLabel } from 'ui/resource-summary/resource-summary-utils';

import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeTypeData } from 'cadenza/workbook/attribute-element/data-type-icon';
import { dataTypeLabel } from 'cadenza/workbook/attribute-element/data-type-icon';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import { icon } from 'cadenza/utils/icon/icon';
import { resourceLabelForProjects } from 'cadenza/project/project-resource-label';
import { createObjecttypeMemberDetailsUrl } from 'cadenza/admin/objecttype-details/objecttype-details-util';

import i18n from './attribute-summary.properties';

/**
 * Attribute representation used in the AttributeSummary component
 *
 * The type is derived from WorkbookAttributeDto:
 * - Some properties are used as is.
 * - Some properties are made optional.
 * - Some properties are added.
 *
 * The component uses other components internally, so this type includes their attribute representations.
 */
export type AttributeSummaryAttribute = Pick<
  WorkbookAttributeDto,
  'levels' | 'objecttypeAttributeName' | 'printName' | 'selectedLevelName' | 'type' | 'projectPrintName' | 'projectKind'
> &
  Partial<Pick<WorkbookAttributeDto, 'category' | 'objecttypeId'>> & {
    objecttypeBoundToWorkbook?: boolean;
    databaseTableColumn?: string;
    description?: string;
  } & AttributeTypeData &
  Partial<AttributeWithObjecttype & DataSourceLabelOptions>;

interface AttributeMetaLabelOptions {
  withDescription?: boolean;
}

interface AttributeResourceSummaryOptions extends AttributeMetaLabelOptions {
  withManagementLink?: boolean;
}

export function createAttributeSummary(
  attribute: AttributeSummaryAttribute,
  { withDescription = false, withManagementLink = false }: AttributeResourceSummaryOptions = {},
) {
  const attributeCategory = getAttributeCategory(attribute);
  const metaLabels: (Node | string)[] = [];
  if (attributeCategory) {
    metaLabels.push(AttributeCategory.i18n(attributeCategory, { count: 1 }));
  }
  metaLabels.push(dataTypeLabel(attribute));
  if (attribute.databaseTableColumn) {
    metaLabels.push(createResourceLabel('column', attribute.databaseTableColumn, { styleClass: 'ellipsis' }));
  }
  const { objecttypePrintName } = attribute;
  if (objecttypePrintName) {
    metaLabels.push(attributeObjecttypeLabel({ objecttypePrintName, ...attribute }));
  }
  if (attribute.projectKind) {
    metaLabels.push(
      resourceLabelForProjects({
        projectKind: attribute.projectKind,
        printName: attribute.projectPrintName!,
      }),
    );
  } else {
    const { dataSourcePrintName, dataSourceType, dataSourceFlavor } = attribute;
    if (dataSourcePrintName && dataSourceType) {
      metaLabels.push(createDataSourceLabel({ dataSourcePrintName, dataSourceType, dataSourceFlavor }));
    }
  }
  if (withDescription && attribute.description) {
    metaLabels.push(createDescription(attribute.description));
  }
  if (attribute.objecttypeBoundToWorkbook) {
    metaLabels.push(i18n('inWorkbookStorage'));
  }
  const header = createAttributeLabel(attribute.type, attributePrintName(attribute), attribute);
  const headlineOptions =
    withManagementLink && attribute.objecttypeId && attribute.objecttypeAttributeName ?
      {
        managementCenterLink: createObjecttypeMemberDetailsUrl(
          attribute.objecttypeId,
          attribute.objecttypeAttributeName,
        ),
        managementCenterLinkLabel: i18n('openInManagementCenter'),
      }
    : undefined;
  return createResourceSummary(header, metaLabels, headlineOptions);
}
function getAttributeCategory(attribute: AttributeSummaryAttribute) {
  if (attribute.selectedLevelName) {
    assertNonNullable(attribute.levels, 'if the attribute has a selected level, it must have levels');
    const selectedLevel = attribute.levels.find((level) => level.name === attribute.selectedLevelName);
    assertNonNullable(selectedLevel, 'selected level is not part of attribute levels');
    return selectedLevel.attributeCategory;
  }
  return attribute.category;
}

function createDescription(description: string) {
  return h('.d-resource-summary--description', [
    icon(infoSvg, { size: 'xs' }),
    h('span.d-resource-summary--description-text', description),
  ]);
}
